// src/components/tutor/mobile/MobileTutorProfile.js
import React, { useState, useEffect, useRef } from "react";
import { auth, db } from "../../../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import {
  LuUser,
  LuClock,
  LuDollarSign,
  LuBell,
  LuSettings,
  LuLogOut,
  LuChevronRight,
  LuChevronLeft,
  LuMoon,
  LuLock,
  LuBookOpen,
} from "react-icons/lu";
import MobileTutorMyInfo from "./MobileTutorMyInfo";
import MobileTutorLogging from "./MobileTutorLogging";
import MobileTutorPayroll from "./MobileTutorPayroll";
import MobileTutorSettings from "./MobileTutorSettings";
import MobileTutorSubjects from "./MobileTutorSubjects";
import "./MobileTutor.css";

// ─── Rows config ─────────────────────────────────────────────────────────────
// Icon colors are intentionally removed — all icons use the same neutral
// palette defined in CSS, consistent with the app-wide monochrome design.

const SECTIONS = [
  {
    items: [
      { key: "myinfo",        label: "Account Details",    Icon: LuUser       },
      { key: "subjects",      label: "My Subjects",        Icon: LuBookOpen   },
      { key: "logging",       label: "Request Work Hours", Icon: LuClock      },
      { key: "payroll",       label: "Payroll History",    Icon: LuDollarSign },
    ],
  },
  {
    items: [
      { key: "notification",  label: "Notifications",      Icon: LuBell       },
      { key: "settings",      label: "Settings",           Icon: LuSettings   },
    ],
  },
];

const SUB_PAGE_TITLES = {
  myinfo:       "Account Details",
  subjects:     "My Subjects",
  logging:      "Request Work Hours",
  payroll:      "Payroll History",
  notification: "Notifications",
  settings:     "Settings",
};

// ─── Settings page ────────────────────────────────────────────────────────────

function ComingSoonRow({ Icon, label }) {
  return (
    <div className="mpr-row mpr-row--last mpr-row--disabled" style={{ opacity: 0.4, cursor: "default" }}>
      <div className="mpr-row-icon">
        <Icon size={15} />
      </div>
      <span className="mpr-row-label">{label}</span>
      <span className="mpr-coming-soon-badge">Soon</span>
    </div>
  );
}

function SettingsPlaceholder() {
  return (
    <div className="mpr-container" style={{ paddingBottom: 60 }}>
      <div className="mpr-sections">
        <div className="mpr-section-card">
          <ComingSoonRow Icon={LuMoon}     label="Appearance" />
        </div>
        <div className="mpr-section-card">
          <ComingSoonRow Icon={LuLock}     label="Change Password" />
        </div>
      </div>
      <p style={{ fontSize: 12, color: "#9CA3AF", margin: "18px 20px 0", lineHeight: 1.55 }}>
        More settings will be available in a future update.
      </p>
    </div>
  );
}

// ─── Sub-page wrapper ─────────────────────────────────────────────────────────

function SubPageWrapper({ title, onBack, children }) {
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
    if (dx > 80 && dx > dy * 1.5) onBack();
    touchStartX.current = null;
    touchStartY.current = null;
  };

  return (
    <div
      className="mpr-subpage"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="mpr-subpage-header">
        <button className="mpr-back-btn" onClick={onBack} aria-label="Go back">
          <LuChevronLeft size={19} />
        </button>
        <h1 className="mpr-subpage-title">{title}</h1>
        {/* Balancing spacer keeps title visually centered */}
        <div className="mpr-back-spacer" aria-hidden="true" />
      </div>
      <div className="mpr-subpage-content">
        {children}
      </div>
    </div>
  );
}

// ─── Profile row ──────────────────────────────────────────────────────────────

function ProfileRow({ label, Icon, onPress, isLast }) {
  return (
    <button
      className={`mpr-row ${isLast ? "mpr-row--last" : ""}`}
      onClick={onPress}
    >
      <div className="mpr-row-icon">
        <Icon size={15} />
      </div>
      <span className="mpr-row-label">{label}</span>
      <LuChevronRight size={14} className="mpr-row-chevron" />
    </button>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function MobileTutorProfile() {
  const [view,              setView]              = useState("main");
  const [profile,           setProfile]           = useState({ name: "", email: "" });
  const [logoutConfirm,     setLogoutConfirm]     = useState(false);
  // Subjects inner navigation: null = category list, string = selected category
  const [subjectsCategory,  setSubjectsCategory]  = useState(null);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    getDoc(doc(db, "users", user.uid))
      .then((snap) => {
        if (snap.exists()) {
          const d = snap.data();
          setProfile({
            name:  d.name  || user.displayName || "Tutor",
            email: d.email || user.email        || "",
          });
        } else {
          setProfile({ name: user.displayName || "Tutor", email: user.email || "" });
        }
      })
      .catch(console.error);
  }, []);

  const initial = profile.name?.trim()?.[0]?.toUpperCase() || "T";

  const handleLogout = () => {
    if (logoutConfirm) {
      auth.signOut().catch(console.error);
    } else {
      setLogoutConfirm(true);
      setTimeout(() => setLogoutConfirm(false), 3000);
    }
  };

  // Sub-page render
  if (view !== "main") {
    // For subjects, title and back-action depend on whether a category is selected
    const subjectsTitle = subjectsCategory ?? SUB_PAGE_TITLES["subjects"];
    const subjectsBack  = subjectsCategory
      ? () => setSubjectsCategory(null)
      : () => setView("main");

    return (
      <SubPageWrapper
        title={view === "subjects" ? subjectsTitle : SUB_PAGE_TITLES[view]}
        onBack={view === "subjects" ? subjectsBack : () => setView("main")}
      >
        {view === "myinfo"        && <MobileTutorMyInfo />}
        {view === "subjects"      && (
          <MobileTutorSubjects
            selectedCategory={subjectsCategory}
            onSelectCategory={setSubjectsCategory}
          />
        )}
        {view === "logging"       && <MobileTutorLogging />}
        {view === "payroll"       && <MobileTutorPayroll />}
        {view === "notification"  && <MobileTutorSettings />}
        {view === "settings"      && <SettingsPlaceholder />}
      </SubPageWrapper>
    );
  }

  // Main profile page
  return (
    <div className="mpr-container">

      {/* ── Header ── */}
      <div className="mpr-header">
        <p className="mpr-page-title">Profile</p>
        <div className="mpr-avatar-wrap">
          <div className="mpr-avatar">{initial}</div>
          <p className="mpr-name">{profile.name}</p>
          <p className="mpr-email">{profile.email}</p>
        </div>
      </div>

      {/* ── Menu sections ── */}
      <div className="mpr-sections">
        {SECTIONS.map((section, si) => (
          <div key={si} className="mpr-section-card">
            {section.items.map((item, ii) => (
              <ProfileRow
                key={item.key}
                label={item.label}
                Icon={item.Icon}
                onPress={() => setView(item.key)}
                isLast={ii === section.items.length - 1}
              />
            ))}
          </div>
        ))}
      </div>

      {/* ── Logout ── */}
      <div className="mpr-logout-wrap">
        <button
          className={`mpr-logout-btn ${logoutConfirm ? "mpr-logout-btn--confirm" : ""}`}
          onClick={handleLogout}
        >
          <LuLogOut size={15} />
          <span>{logoutConfirm ? "Tap again to confirm" : "Log Out"}</span>
        </button>
      </div>
    </div>
  );
}
